/**
 * Wave 29 Chunk 8d — Project KPI Dashboard (Dirk bug 2026-05-11).
 *
 * Route: /projects/:id/dashboard
 *
 * "Where is the project dashboard showing KPI like budgets, time effectiveness,
 *  trips planned vs trips used (found from sales order km vs job card trips)?"
 *
 * Single GET to /api/projects/{id}/dashboard returns a structured roll-up:
 *  - budget (effective, expense, PO, committed, burn %, status band)
 *  - time (planned vs actual start/end, schedule variance days)
 *  - trips (planned km from internal/DNU sales-order line items VS used trips
 *    from JC start_location check-ins)
 *  - productivity (JC + task counts)
 *  - quality (avg customer rating from JC signoffs)
 *
 * Tech-safe view returned automatically by the backend when role=technician.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Banknote, CalendarDays, Truck, ClipboardList, Star, AlertTriangle,
  TrendingUp, TrendingDown, Clock, Loader2,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_BAND_LABELS = {
  on_track: { label: "On track", color: "emerald" },
  caution: { label: "Caution", color: "amber" },
  warning: { label: "Warning", color: "orange" },
  over: { label: "Over budget", color: "rose" },
};

export default function ProjectDashboard() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/projects/${id}/dashboard`);
        if (alive) setData(d);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || e?.message || "Failed to load dashboard.");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [id]);

  if (loading) return <div className="h-40 hv-card animate-pulse" data-testid="proj-dash-loading" />;
  if (err) return (
    <div className="hv-card p-6 text-center text-sm text-rose-300" data-testid="proj-dash-error">
      <AlertTriangle className="w-5 h-5 mx-auto mb-2" /> {err}
    </div>
  );
  if (!data) return null;

  const isTech = user?.role === "technician" || data.scrubbed;

  return (
    <div className="space-y-4" data-testid="project-dashboard-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to={`/projects/${id}`} className="hv-btn hv-btn-ghost text-xs" data-testid="proj-dash-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to project
        </Link>
        <h1 className="font-display text-xl sm:text-2xl font-bold truncate">
          {data.project.title}
        </h1>
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">
          {data.project.status}
        </span>
      </div>

      {/* Budget — managers+/reps only */}
      {!isTech && data.budget && (
        <BudgetCard b={data.budget} currency={data.budget.budget_currency} source={data.project.budget_source} />
      )}

      {/* Time */}
      {data.time && <TimeCard t={data.time} />}

      {/* Trips planned vs used */}
      {!isTech && data.trips && <TripsCard tr={data.trips} />}

      {/* Productivity */}
      {data.productivity && <ProductivityCard p={data.productivity} isTech={isTech} />}

      {/* Quality */}
      {!isTech && data.quality && <QualityCard q={data.quality} />}
    </div>
  );
}

function StatCell({ label, value, sub, accent }) {
  return (
    <div className="hv-card !p-3 min-w-[110px]">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${accent || "text-white"}`}>{value}</div>
      {sub && <div className="text-[10px] text-[#647087]">{sub}</div>}
    </div>
  );
}

function fmtMoney(n, ccy = "ZAR") {
  if (n == null) return "—";
  return `${ccy} ${Number(n).toLocaleString("en-ZA", { maximumFractionDigits: 2 })}`;
}

function BudgetCard({ b, currency, source }) {
  const band = STATUS_BAND_LABELS[b.status_band] || { label: "—", color: "slate" };
  return (
    <section className="hv-card !p-4 space-y-3" data-testid="proj-dash-budget">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Banknote className="w-4 h-4 text-[#1FB6C1]" /> Budget & burn
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[#647087]">Source: {source || "manual"}</span>
          <span
            className={`text-[10px] px-2 py-0.5 rounded-full border border-${band.color}-400/40 text-${band.color}-300 bg-${band.color}-500/10`}
            data-testid="proj-dash-status-band"
          >
            {band.label}
          </span>
        </div>
      </header>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatCell label="Effective budget" value={fmtMoney(b.effective_budget, currency)} />
        <StatCell label="Expenses (paid)" value={fmtMoney(b.expense_total, currency)} sub={`${b.approved_expenses_count || 0} approved · ${b.pending_expenses_count || 0} pending`} />
        <StatCell label="POs committed" value={fmtMoney(b.purchase_order_total, currency)} />
        <StatCell label="Committed cost" value={fmtMoney(b.committed_cost_total, currency)} sub="POs + approved expenses" />
        <StatCell label="Revenue" value={fmtMoney(b.revenue_total, currency)} />
        <StatCell
          label="Margin"
          value={b.margin_pct != null ? `${b.margin_pct}%` : "—"}
          sub={b.margin_amount != null ? fmtMoney(b.margin_amount, currency) : null}
          accent={b.margin_amount != null && b.margin_amount < 0 ? "text-rose-300" : "text-emerald-300"}
        />
        <StatCell
          label="Burn"
          value={b.burn_pct != null ? `${b.burn_pct}%` : "—"}
          sub={b.daily_burn != null ? `${fmtMoney(b.daily_burn, currency)}/day` : null}
          accent={
            b.burn_pct >= 100 ? "text-rose-300" :
            b.burn_pct >= 90 ? "text-orange-300" :
            b.burn_pct >= 70 ? "text-amber-300" : "text-emerald-300"
          }
        />
      </div>
    </section>
  );
}

function TimeCard({ t }) {
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-ZA") : "—";
  const onTime = t.is_on_time;
  return (
    <section className="hv-card !p-4 space-y-3" data-testid="proj-dash-time">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-[#1FB6C1]" /> Time effectiveness
        </h2>
        {onTime != null && (
          <span
            className={`text-[10px] px-2 py-0.5 rounded-full border ${onTime ? "border-emerald-400/40 text-emerald-300 bg-emerald-500/10" : "border-rose-400/40 text-rose-300 bg-rose-500/10"}`}
            data-testid="proj-dash-time-band"
          >
            {onTime ? "On time" : `Slipped ${t.schedule_variance_days}d`}
          </span>
        )}
      </header>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatCell label="Planned start" value={fmt(t.planned_start)} />
        <StatCell label="Actual start" value={fmt(t.actual_start)} sub={t.actual_start ? "First JC checked-in" : "Not started"} />
        <StatCell label="Planned end" value={fmt(t.planned_end)} />
        <StatCell label="Actual end" value={fmt(t.actual_end)} sub={t.actual_end ? "Last JC closed" : "In progress"} />
      </div>
      {t.schedule_variance_days != null && (
        <div className="text-[11px] text-[#a0aabf]">
          Schedule variance: <b className={t.schedule_variance_days > 0 ? "text-rose-300" : "text-emerald-300"}>
            {t.schedule_variance_days > 0 ? `+${t.schedule_variance_days}` : t.schedule_variance_days}d
          </b> {t.schedule_variance_days > 0 ? <TrendingDown className="w-3 h-3 inline" /> : <TrendingUp className="w-3 h-3 inline" />}
        </div>
      )}
    </section>
  );
}

function TripsCard({ tr }) {
  const overrun = tr.variance_km != null && tr.variance_km > tr.planned_km * 0.1; // 10% threshold
  return (
    <section className="hv-card !p-4 space-y-3" data-testid="proj-dash-trips">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Truck className="w-4 h-4 text-[#1FB6C1]" /> Trips planned vs used
        </h2>
        {tr.variance_km != null && (
          <span
            className={`text-[10px] px-2 py-0.5 rounded-full border ${overrun ? "border-rose-400/40 text-rose-300 bg-rose-500/10" : "border-emerald-400/40 text-emerald-300 bg-emerald-500/10"}`}
            data-testid="proj-dash-trips-band"
          >
            {overrun ? `+${tr.variance_km}km over` : `${tr.variance_km}km variance`}
          </span>
        )}
      </header>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatCell label="Planned km" value={`${tr.planned_km} km`} sub="from internal sales orders" />
        <StatCell label="Trips used" value={tr.used_trips} sub="JCs with tech check-in" />
        <StatCell label="Distance covered" value={`${tr.used_distance_km} km`} sub="check-in vs saved address" />
        {tr.variance_km != null && (
          <StatCell
            label="Variance"
            value={`${tr.variance_km > 0 ? "+" : ""}${tr.variance_km} km`}
            accent={overrun ? "text-rose-300" : "text-emerald-300"}
          />
        )}
      </div>
      {tr.planned_km === 0 && (
        <p className="text-[11px] text-[#647087]">
          ℹ️ No `km` recorded on any internal sales-order line item for this project — add km/distance to your DNU sales orders to enable trip variance tracking.
        </p>
      )}
    </section>
  );
}

function ProductivityCard({ p, isTech }) {
  const j = p.jobcards || {};
  const t = p.tasks || {};
  return (
    <section className="hv-card !p-4 space-y-3" data-testid="proj-dash-productivity">
      <header className="text-sm font-semibold flex items-center gap-2">
        <ClipboardList className="w-4 h-4 text-[#1FB6C1]" /> Productivity
      </header>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {isTech ? (
          <StatCell label="My job cards" value={j.assigned_to_me ?? 0} />
        ) : (
          <>
            <StatCell label="JC total" value={j.total ?? 0} />
            <StatCell label="JC done" value={j.completed ?? 0} accent="text-emerald-300" />
            <StatCell label="In progress" value={j.in_progress ?? 0} accent="text-amber-300" />
            <StatCell label="Open" value={j.open ?? 0} />
            <StatCell label="Cancelled" value={j.cancelled ?? 0} accent="text-rose-300" />
          </>
        )}
      </div>
      {!isTech && (
        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-white/5">
          <StatCell label="Tasks total" value={t.total ?? 0} />
          <StatCell label="Tasks done" value={t.done ?? 0} accent="text-emerald-300" />
          <StatCell label="Tasks open" value={t.open ?? 0} accent="text-amber-300" />
        </div>
      )}
    </section>
  );
}

function QualityCard({ q }) {
  return (
    <section className="hv-card !p-4 space-y-3" data-testid="proj-dash-quality">
      <header className="text-sm font-semibold flex items-center gap-2">
        <Star className="w-4 h-4 text-[#1FB6C1]" /> Quality signals
      </header>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <StatCell
          label="Avg customer rating"
          value={q.avg_customer_rating != null ? `${q.avg_customer_rating}/5` : "—"}
          sub={q.ratings_count ? `${q.ratings_count} signoffs` : "No signoffs yet"}
          accent={q.avg_customer_rating >= 4 ? "text-emerald-300" : q.avg_customer_rating >= 3 ? "text-amber-300" : q.avg_customer_rating != null ? "text-rose-300" : "text-white"}
        />
      </div>
    </section>
  );
}
